import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'semantic-ui-react';
import _ from 'lodash';
import classNames from 'classnames';

import {
  CHEVRON_COLUMN_WIDTH,
  CommonSectionPropTypes,
} from 'Pages/manageproduction/helpers/Constants';
import { isLeftover } from 'Pages/manageproduction/helpers/productionRecord';

class CountSection extends PureComponent {
  static propTypes = {
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        fieldName: PropTypes.string,
        width: PropTypes.number,
      })
    ),
    collapsedColumns: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        fieldName: PropTypes.string,
        width: PropTypes.number,
      })
    ),
    className: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    ...CommonSectionPropTypes,
    disableEditingWhenLeftover: PropTypes.bool,
    headerProps: PropTypes.object,
    rowProps: PropTypes.object,
  };

  static defaultProps = {
    disableEditingWhenLeftover: false,
    headerProps: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      collapsed: false,
      commonRowStyle: this.generateCommonRowStyles(props.columns),
      collapsedRowStyle: this.generateCommonRowStyles(props.collapsedColumns),
      animationStyle: {},
    };
    this.containerRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const {
      columns: prevColumns,
      collapsedColumns: prevCollapsedColumns,
    } = prevProps;
    const { columns, collapsedColumns } = this.props;

    if (prevColumns !== columns) {
      this.setState({
        commonRowStyle: this.generateCommonRowStyles(columns),
      });
    }
    if (prevCollapsedColumns !== collapsedColumns) {
      this.setState({
        collapsedRowStyle: this.generateCommonRowStyles(collapsedColumns),
      });
    }
  }

  componentWillUnmount() {
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
    }
  }

  generateCommonRowStyles(columns) {
    const iconColumnWidth = `${CHEVRON_COLUMN_WIDTH}px`;
    return {
      gridTemplateColumns: columns
        .map((c) => `${c.width}px`)
        .concat([iconColumnWidth])
        .join(' '),
    };
  }

  get commonRowStyle() {
    const { collapsed, commonRowStyle, collapsedRowStyle } = this.state;

    return collapsed ? collapsedRowStyle : commonRowStyle;
  }

  get columns() {
    const { columns, collapsedColumns } = this.props;
    const { collapsed } = this.state;

    return collapsed ? collapsedColumns : columns;
  }

  renderRows = () => {
    const {
      isReadOnly,
      disableEditingWhenLeftover,
      detailsByRecipeCategory,
      RowComponent,
      HeaderComponent,
      gridFields,
      onInputChange,
      headerProps,
      rowProps,
    } = this.props;
    const { collapsed } = this.state;
    let itemsToRender = [];

    Object.keys(detailsByRecipeCategory).forEach((recipeCategoryId) => {
      const detailsInCategory = detailsByRecipeCategory[recipeCategoryId];

      const headerRow = (
        <div
          className="divider-row"
          key={`category-${recipeCategoryId}`}
          style={this.commonRowStyle}
        >
          <HeaderComponent
            recipeCategoryId={recipeCategoryId}
            isReadOnly={isReadOnly}
            collapsed={collapsed}
            onInputChange={onInputChange}
            {...headerProps}
          />
        </div>
      );
      itemsToRender.push(headerRow);

      detailsInCategory.forEach((detail, i) => {
        itemsToRender.push(
          <div
            className="grid-row"
            key={`category-${recipeCategoryId}-${i}`}
            style={this.commonRowStyle}
          >
            <RowComponent
              {..._.pick(detail, gridFields)}
              isReadOnly={
                isReadOnly || (disableEditingWhenLeftover && isLeftover(detail))
              }
              adjustPercentage={this.adjustPercentage}
              collapsed={collapsed}
              onInputChange={onInputChange}
              {...rowProps}
            />
          </div>
        );
      });
    });

    return itemsToRender;
  };

  get gridGap() {
    const { current } = this.containerRef;

    if (current) {
      const gridGap =
        parseInt(
          getComputedStyle(current).getPropertyValue('--grid-column-gap'),
          10
        ) || 0;

      return gridGap;
    }
    return 0;
  }

  get animationWidths() {
    const { columns, collapsedColumns } = this.props;
    const { collapsed } = this.state;
    const numberOfColumns = columns.length;
    const numberOfCollapsedColumns = collapsedColumns.length;

    // Don't subtract 1 from the number of columns since it includes the chevron column
    const padding = 16;
    const gapWidth = this.gridGap * numberOfColumns;
    const collapsedGapWidth = this.gridGap * numberOfCollapsedColumns;
    const sectionWidth =
      _.sumBy(columns, (c) => c.width) +
      gapWidth +
      CHEVRON_COLUMN_WIDTH +
      padding;
    const collapsedSectionWidth =
      _.sumBy(collapsedColumns, (c) => c.width) +
      collapsedGapWidth +
      CHEVRON_COLUMN_WIDTH +
      padding;

    let startWidth;
    let endWidth;

    if (collapsed) {
      startWidth = collapsedSectionWidth;
      endWidth = sectionWidth;
    } else {
      startWidth = sectionWidth;
      endWidth = collapsedSectionWidth;
    }

    return { startWidth, endWidth };
  }

  animateCollapse = () => {
    const { collapsed } = this.state;
    const { startWidth, endWidth } = this.animationWidths;

    const updatedCollapsed = !collapsed;
    const animationStartState = {
      animationStyle: { width: startWidth },
      animating: true,
    };

    // Want to keep the column containers rendered when going to
    // collapsed state
    if (updatedCollapsed) {
      animationStartState.collapsed = updatedCollapsed;
    }

    this.setState(animationStartState, () => {
      setTimeout(() => {
        this.setState({
          animationStyle: { width: endWidth },
        });
      }, 125);
      setTimeout(() => {
        const animationEndState = {
          animationStyle: {},
          animating: false,
        };

        // Show column containers when expanding the section
        if (!updatedCollapsed) {
          animationEndState.collapsed = updatedCollapsed;
        }

        this.setState(animationEndState);
      }, 875);
    });
  };

  onCollapseIconClick = () => {
    const { animating } = this.state;

    if (!animating) {
      this.animateCollapse();
    }
  };

  render() {
    const { className, gridStyle, title } = this.props;
    const { collapsed, animationStyle, animating } = this.state;
    const gridContainerStyle = { ...animationStyle, ...gridStyle };

    return (
      <div
        className={className}
        ref={this.containerRef}
        style={gridContainerStyle}
      >
        <div className="grid-row grid-title-row" style={this.commonRowStyle}>
          {title}
        </div>
        <div className="grid-header grid-row" style={this.commonRowStyle}>
          {this.columns.map((c, i) => (
            <div className="grid-item" key={i}>
              {i > 0 && i < this.columns.length && animating ? null : c.label}
            </div>
          ))}
          <div className="grid-item">
            {!animating && (
              <Icon
                className={classNames('chevron', {
                  left: !collapsed,
                  right: collapsed,
                })}
                link
                onClick={this.onCollapseIconClick}
              />
            )}
          </div>
        </div>
        {this.renderRows()}
      </div>
    );
  }
}

export { CountSection };



// WEBPACK FOOTER //
// ./src/pages/manageproduction/subpages/editproduction/components/CountSection.js